import { parseFxSnapshot, type FxSnapshot } from '@carcheck/shared';
import dayjs from 'dayjs';

import type { RawRecord } from './rawRecord';
import { ADMIN_TIMEZONE } from '../../../utils/format';

/**
 * AUT-542 — the `specialists.fx` form (RULINGS 11). Rates accept a comma
 * («3,27» → 3.27); an empty EUR leaves no `bynPerEur`; `asOf` is a Minsk
 * `YYYY-MM-DD` not later than today in Minsk; a save writes
 * `source: 'manual'`. The stored record is edited as a copy, so fields the
 * form does not know are kept. `parseFxSnapshot` checks only the fields it
 * knows (a newer api may store more) — its result is never sent.
 */

export interface FxFormValues {
  usd: string;
  eur: string;
  /** `YYYY-MM-DD` or '' (the date input's value). */
  asOf: string;
}

const DECIMAL_RE = /^\d+([.,]\d+)?$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const FX_FIELDS = ['bynPerUsd', 'bynPerEur', 'asOf', 'source'] as const;

/** «3,27» / «3.27» → 3.27; anything else (empty included) → null. */
export const parseDecimal = (s: string): number | null => {
  const trimmed = s.trim();
  if (!DECIMAL_RE.test(trimmed)) return null;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/** Today's date in Minsk as `YYYY-MM-DD`. */
export const minskToday = (now: Date): string => dayjs(now).tz(ADMIN_TIMEZONE).format('YYYY-MM-DD');

/** A stored `asOf` (date or ISO timestamp) as a Minsk `YYYY-MM-DD`. */
export const minskDateOf = (asOf: string): string =>
  DATE_RE.test(asOf) ? asOf : dayjs(asOf).tz(ADMIN_TIMEZONE).format('YYYY-MM-DD');

const knownFields = (record: RawRecord): RawRecord => {
  const out: RawRecord = {};
  for (const field of FX_FIELDS) if (field in record) out[field] = record[field];
  return out;
};

/** The record to send, or null when the form cannot be saved. */
export const buildFxRecord = (
  prev: RawRecord | null,
  form: FxFormValues,
  now: Date,
): RawRecord | null => {
  const usd = parseDecimal(form.usd);
  if (usd === null) return null;
  const eurText = form.eur.trim();
  const eur = eurText === '' ? null : parseDecimal(eurText);
  if (eurText !== '' && eur === null) return null;
  if (!DATE_RE.test(form.asOf) || form.asOf > minskToday(now)) return null;

  const next: RawRecord = { ...(prev ?? {}), bynPerUsd: usd, asOf: form.asOf, source: 'manual' };
  if (eur === null) delete next.bynPerEur;
  else next.bynPerEur = eur;

  return parseFxSnapshot(knownFields(next)).ok ? next : null;
};

const commaNumber = (n: number | undefined): string =>
  n === undefined ? '' : String(n).replace('.', ',');

/** The stored rate, read through the shared parser (known fields only); null = «Курс не задан». */
export const readFx = (value: unknown): FxSnapshot | null => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  const parsed = parseFxSnapshot(knownFields(value as RawRecord));
  return parsed.ok ? parsed.value : null;
};

/** Form start values: the stored rate with commas and its Minsk date; empty for no rate. */
export const fxFormSeed = (value: unknown): FxFormValues => {
  const fx = readFx(value);
  if (!fx) return { usd: '', eur: '', asOf: '' };
  return {
    usd: commaNumber(fx.bynPerUsd),
    eur: commaNumber(fx.bynPerEur),
    asOf: minskDateOf(fx.asOf),
  };
};
